/**
 * useProjectDraft — estado transiente do formulário de criação de projeto.
 *
 * Mantém o rascunho (dados + fases + tarefas + notificações) em `useState` e
 * expõe reducers memoizados, além de:
 *  - `toCreateRequest()` — converte o rascunho em `CreateProjectRequest`,
 *    resolvendo as dependências de `localId` (referência estável no frontend)
 *    para `taskIndex` (índice do array `tasks`, contrato do backend);
 *  - `validateDraft()` — valida por aba, retornando os erros agrupados para o
 *    `ProjectFormPage` posicionar o usuário na aba correta.
 *
 * As tarefas guardam só cronograma no draft — título/responsável/datas viram o
 * ticket materializado no backend (fonte única).
 */

import { useCallback, useState } from 'react';

import {
  PROJECT_DEFAULT_MESSAGE_TEMPLATE,
  type CreateProjectRequest,
  type CreateProjectTaskInput,
  type DependencyMode,
  type ProjectInternalNotifyConfig,
  type ProjectNotifyEvents,
  type ProjectPhase,
} from '../types';

/** Aba do formulário — usada para posicionar erros de validação. */
export type ProjectFormTab = 'dados' | 'cronograma' | 'contatos';

/** Paleta ciclada ao adicionar novas fases (D-visual: primeira fase = roxo). */
export const PHASE_COLORS = [
  '#8b5cf6',
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#ec4899',
  '#14b8a6',
  '#f97316',
] as const;

/** Tarefa no rascunho — dependências referenciam `localId` (não índice). */
export interface DraftTask {
  /** uuid local, estável enquanto o rascunho existe (base das dependências). */
  localId: string;
  title: string;
  assigneeId?: string;
  phaseId: string;
  /** `yyyy-mm-dd` (valor de `<input type="date">`). */
  startDate: string;
  /** `yyyy-mm-dd`. Igual a `startDate` quando `isMilestone`. */
  endDate: string;
  isMilestone: boolean;
  dependencies: { localId: string; mode: DependencyMode }[];
  existingTicketId?: string;
}

/** Rascunho completo do projeto (estado do formulário). */
export interface ProjectDraft {
  name: string;
  /** Vínculo com `customers` (ref). String vazia = "— Sem cliente —". */
  clientId: string;
  color: string;
  pipelineId: string;
  leaderId: string;
  memberIds: string[];
  phases: ProjectPhase[];
  tasks: DraftTask[];
  contactIds: string[];
  channelId?: string;
  events: ProjectNotifyEvents;
  messageTemplate: string;
  internal: ProjectInternalNotifyConfig;
}

export interface ValidationGroup {
  tab: ProjectFormTab;
  errors: string[];
}

export interface UseProjectDraftReturn {
  value: ProjectDraft;
  /** Setter genérico para campos escalares (name, clientId, color, pipelineId, channelId, messageTemplate). */
  setField: <K extends keyof ProjectDraft>(key: K, val: ProjectDraft[K]) => void;
  /** Define o líder e remove-o automaticamente da lista de membros. */
  setLeader: (leaderId: string) => void;
  setMemberIds: (ids: string[]) => void;
  setContactIds: (ids: string[]) => void;
  addPhase: () => void;
  renamePhase: (phaseId: string, name: string) => void;
  updatePhase: (phaseId: string, patch: { name?: string; color?: string }) => void;
  removePhase: (phaseId: string) => void;
  addTask: (phaseId: string, task: Omit<DraftTask, 'localId' | 'phaseId'>) => void;
  removeTask: (localId: string) => void;
  updateTaskField: (localId: string, patch: Partial<Omit<DraftTask, 'localId'>>) => void;
  toggleEvent: (key: keyof ProjectNotifyEvents) => void;
  setInternal: (key: keyof ProjectInternalNotifyConfig, val: boolean) => void;
  toCreateRequest: () => CreateProjectRequest;
  validateDraft: () => ValidationGroup[];
}

/** uuid local (fallback simples caso `crypto.randomUUID` não exista). */
function localId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `t_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
}

/** `yyyy-mm-dd` → ISO UTC-midnight (o backend normaliza para UTC-midnight). */
function dateToIso(d: string): string {
  if (!d) return new Date().toISOString();
  const parsed = new Date(`${d}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
}

function makeDefaultDraft(): ProjectDraft {
  return {
    name: '',
    clientId: '',
    color: PHASE_COLORS[0],
    pipelineId: '',
    leaderId: '',
    memberIds: [],
    phases: [{ id: localId(), name: 'Fase 1', color: PHASE_COLORS[0], order: 0 }],
    tasks: [],
    contactIds: [],
    events: {
      milestoneCompleted: true,
      phaseCompleted: true,
      projectCompleted: true,
      taskCompleted: false,
    },
    messageTemplate: PROJECT_DEFAULT_MESSAGE_TEMPLATE,
    internal: { unblocked: true, completedLeader: true, overdue: true },
  };
}

export function useProjectDraft(): UseProjectDraftReturn {
  const [value, setValue] = useState<ProjectDraft>(makeDefaultDraft);

  const setField = useCallback(<K extends keyof ProjectDraft>(key: K, val: ProjectDraft[K]) => {
    setValue((prev) => ({ ...prev, [key]: val }));
  }, []);

  const setLeader = useCallback((leaderId: string) => {
    setValue((prev) => ({
      ...prev,
      leaderId,
      memberIds: prev.memberIds.filter((id) => id !== leaderId),
    }));
  }, []);

  const setMemberIds = useCallback((ids: string[]) => {
    setValue((prev) => ({ ...prev, memberIds: ids }));
  }, []);

  const setContactIds = useCallback((ids: string[]) => {
    setValue((prev) => ({ ...prev, contactIds: ids }));
  }, []);

  const addPhase = useCallback(() => {
    setValue((prev) => {
      const nextIndex = prev.phases.length;
      const color = PHASE_COLORS[nextIndex % PHASE_COLORS.length] as string;
      const phase: ProjectPhase = {
        id: localId(),
        name: `Fase ${nextIndex + 1}`,
        color,
        order: nextIndex,
      };
      return { ...prev, phases: [...prev.phases, phase] };
    });
  }, []);

  const renamePhase = useCallback((phaseId: string, name: string) => {
    setValue((prev) => ({
      ...prev,
      phases: prev.phases.map((p) => (p.id === phaseId ? { ...p, name } : p)),
    }));
  }, []);

  const updatePhase = useCallback((phaseId: string, patch: { name?: string; color?: string }) => {
    setValue((prev) => ({
      ...prev,
      phases: prev.phases.map((p) => (p.id === phaseId ? { ...p, ...patch } : p)),
    }));
  }, []);

  const removePhase = useCallback((phaseId: string) => {
    setValue((prev) => {
      const removedTaskIds = new Set(
        prev.tasks.filter((t) => t.phaseId === phaseId).map((t) => t.localId),
      );
      const tasks = prev.tasks
        .filter((t) => t.phaseId !== phaseId)
        .map((t) => ({
          ...t,
          dependencies: t.dependencies.filter((d) => !removedTaskIds.has(d.localId)),
        }));
      return {
        ...prev,
        phases: prev.phases
          .filter((p) => p.id !== phaseId)
          .map((p, i) => ({ ...p, order: i })),
        tasks,
      };
    });
  }, []);

  const addTask = useCallback(
    (phaseId: string, task: Omit<DraftTask, 'localId' | 'phaseId'>) => {
      setValue((prev) => ({
        ...prev,
        tasks: [...prev.tasks, { ...task, localId: localId(), phaseId }],
      }));
    },
    [],
  );

  const removeTask = useCallback((id: string) => {
    setValue((prev) => ({
      ...prev,
      tasks: prev.tasks
        .filter((t) => t.localId !== id)
        .map((t) => ({
          ...t,
          dependencies: t.dependencies.filter((d) => d.localId !== id),
        })),
    }));
  }, []);

  const updateTaskField = useCallback(
    (id: string, patch: Partial<Omit<DraftTask, 'localId'>>) => {
      setValue((prev) => ({
        ...prev,
        tasks: prev.tasks.map((t) => {
          if (t.localId !== id) return t;
          const next = { ...t, ...patch };
          // Marco: data única (fim = início).
          if (next.isMilestone) next.endDate = next.startDate;
          return next;
        }),
      }));
    },
    [],
  );

  const toggleEvent = useCallback((key: keyof ProjectNotifyEvents) => {
    setValue((prev) => ({
      ...prev,
      events: { ...prev.events, [key]: !prev.events[key] },
    }));
  }, []);

  const setInternal = useCallback((key: keyof ProjectInternalNotifyConfig, val: boolean) => {
    setValue((prev) => ({ ...prev, internal: { ...prev.internal, [key]: val } }));
  }, []);

  const toCreateRequest = useCallback((): CreateProjectRequest => {
    const idxOf = new Map(value.tasks.map((t, i) => [t.localId, i] as const));

    const tasks: CreateProjectTaskInput[] = value.tasks.map((t) => {
      const deps = t.dependencies
        .filter((d) => idxOf.has(d.localId))
        .map((d) => ({ taskIndex: idxOf.get(d.localId) as number, mode: d.mode }));

      const task: CreateProjectTaskInput = {
        title: t.title.trim(),
        phaseId: t.phaseId,
        startDate: dateToIso(t.startDate),
        endDate: dateToIso(t.isMilestone ? t.startDate : t.endDate),
        isMilestone: t.isMilestone,
      };
      if (t.assigneeId) task.assigneeId = t.assigneeId;
      if (deps.length > 0) task.dependencies = deps;
      if (t.existingTicketId) task.existingTicketId = t.existingTicketId;
      return task;
    });

    const req: CreateProjectRequest = {
      name: value.name.trim(),
      color: value.color,
      pipelineId: value.pipelineId,
      leaderId: value.leaderId,
      memberIds: value.memberIds,
      phases: value.phases.map((p, i) => ({
        id: p.id,
        name: p.name.trim() || `Fase ${i + 1}`,
        color: p.color,
        order: i,
      })),
      tasks,
      contactIds: value.contactIds,
      notifyConfig: {
        events: value.events,
        messageTemplate: value.messageTemplate,
        ...(value.channelId ? { channelId: value.channelId } : {}),
      },
      internalNotifyConfig: value.internal,
    };
    if (value.clientId) req.clientId = value.clientId;
    return req;
  }, [value]);

  const validateDraft = useCallback((): ValidationGroup[] => {
    const groups: ValidationGroup[] = [];

    const dados: string[] = [];
    if (value.name.trim().length < 2) {
      dados.push('Informe um nome de projeto com pelo menos 2 caracteres.');
    }
    if (!value.pipelineId) dados.push('Selecione o pipeline dos tickets.');
    if (!value.leaderId) dados.push('Selecione o líder do projeto.');
    if (dados.length > 0) groups.push({ tab: 'dados', errors: dados });

    const crono: string[] = [];
    if (value.tasks.length === 0) {
      crono.push('Adicione pelo menos uma tarefa ao cronograma.');
    }
    const known = new Set(value.tasks.map((t) => t.localId));
    const untitled = value.tasks.find((t) => !t.title.trim());
    if (untitled) crono.push('Toda tarefa precisa de um título.');
    const badDates = value.tasks.find(
      (t) => !t.startDate || !t.endDate || (!t.isMilestone && t.endDate < t.startDate),
    );
    if (badDates) {
      crono.push(
        `Confira as datas da tarefa "${badDates.title.trim() || 'sem título'}" (início e fim válidos).`,
      );
    }
    const brokenDep = value.tasks.find((t) =>
      t.dependencies.some((d) => !known.has(d.localId)),
    );
    if (brokenDep) crono.push('Há dependências apontando para tarefas que não existem mais.');
    if (crono.length > 0) groups.push({ tab: 'cronograma', errors: crono });

    // Aba "Contatos & Notificações" não tem validação bloqueante.
    return groups;
  }, [value]);

  return {
    value,
    setField,
    setLeader,
    setMemberIds,
    setContactIds,
    addPhase,
    renamePhase,
    updatePhase,
    removePhase,
    addTask,
    removeTask,
    updateTaskField,
    toggleEvent,
    setInternal,
    toCreateRequest,
    validateDraft,
  };
}
